import { 
  uploadFileToSupabaseS3, 
  generateSignedDownloadUrl, 
  deleteFileFromSupabaseS3,
  extractKeyFromUrl,
  BUCKET_NAMES 
} from '@/integrations/supabase/s3-client';
import { supabase } from '@/integrations/supabase/client';
import { logActivity, ActivityActions } from '@/utils/activityLogger';

// Type definition for bucket names
type BucketType = 'project-images' | 'profile-avatars' | 'site-photos' | 'documents';

/**
 * Uploads a file to Supabase Storage using S3 API with user-specific access
 * @param file The file to upload
 * @param bucket The storage bucket to upload to
 * @param path Optional path prefix (e.g. project ID)
 * @returns URL of the uploaded file
 */
export async function uploadFile(
  file: File,
  bucket: BucketType,
  path?: string
): Promise<string> {
  try {
    
    
    
    // Validate file before upload
    if (!file.size) {
      
      throw new Error('Invalid file: file has zero size');
    }
    
    if (file.size > 10 * 1024 * 1024) { // 10MB max
      
      throw new Error('File size exceeds the 10MB limit');
    }

    

    // Upload to Supabase Storage using S3 API
    const fileUrl = await uploadFileToSupabaseS3(file, bucket, path);
    
    

    // Log the activity
    try {
      
      const { data: { user } } = await supabase.auth.getUser();
      if (user) {
        await logActivity(
          user.id,
          ActivityActions.UPLOAD_FILE,
          {
            bucket,
            path,
            fileName: file.name,
            fileSize: file.size,
            fileType: file.type,
            fileUrl: fileUrl,
          }
        );
        
      } else {
        
      }
    } catch (logError) {
      
    }
    
    
    return fileUrl;
  } catch (error) {
    
    throw error;
  }
}

/**
 * Uploads a site photo and creates a record in the database
 * @param file The image file
 * @param projectId The project ID
 * @param description Optional description of the photo
 * @returns The new site photo record ID
 */
export async function uploadSitePhoto(
  file: File,
  projectId: string,
  description?: string
): Promise<string> {
  try {
    
    
    // Upload the file to Supabase Storage
    const photoUrl = await uploadFile(file, 'site-photos', projectId);

    // Create a record in the site_photos table using the RPC function
    const { data, error } = await supabase.rpc('upload_site_photo', {
      project_id: projectId,
      description: description || '',
      photo_url: photoUrl,
    });

    if (error) {
      throw error;
    }

    
    return data;
  } catch (error) {
    
    throw error;
  }
}

/**
 * Gets project images from the database
 * @param projectId The project ID
 * @returns Array of project images
 */
export async function getProjectImages(projectId: string) {
  try {
    const { data, error } = await supabase
      .from('site_photos')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false });

    if (error) {
      throw error;
    }

    return data || [];
  } catch (error) {
    
    throw error;
  }
}

/**
 * Uploads a document and returns its URL
 * @param file The document file to upload
 * @param projectId The related project ID
 * @returns URL of the uploaded document
 */
export async function uploadDocument(
  file: File,
  projectId: string
): Promise<string> {
  
  return uploadFile(file, 'documents', `projects/${projectId}`);
}

/**
 * Upload a profile avatar image
 * @param file The image file
 * @param userId The user ID
 * @returns URL of the uploaded avatar
 */
export async function uploadProfileAvatar(
  file: File,
  userId: string
): Promise<string> {
  
  return uploadFile(file, 'profile-avatars', userId);
}

/**
 * Deletes a file from Supabase Storage
 * @param fileUrl The URL of the file to delete
 * @param bucket The storage bucket containing the file
 */
export async function deleteFile(
  fileUrl: string,
  bucket: BucketType
): Promise<void> {
  try {
    
    
    const key = extractKeyFromUrl(fileUrl);
    if (!key) {
      throw new Error('Could not extract file key from URL');
    }

    await deleteFileFromSupabaseS3(bucket, key);
    
  } catch (error) {
    
    throw error;
  }
}

/**
 * Generate a signed URL for secure file download
 * @param fileUrl The public URL of the file
 * @param bucket The storage bucket
 * @param expiresInSeconds How long the signed URL should be valid (default: 1 hour)
 * @returns Signed URL for secure download
 */
export async function getSignedDownloadUrl(
  fileUrl: string,
  bucket: BucketType,
  expiresInSeconds: number = 3600
): Promise<string> {
  try {
    const key = extractKeyFromUrl(fileUrl);
    if (!key) {
      throw new Error('Could not extract file key from URL');
    }

    return await generateSignedDownloadUrl(bucket, key, expiresInSeconds);
  } catch (error) {
    
    throw error;
  }
}

/**
 * Lists all files in a specific path (user-scoped)
 * Note: This would require implementing list operations in the S3 client
 * For now, returns empty array as placeholder
 */
export async function listFiles(
  bucket: BucketType,
  path?: string
) {
  try {
    
    
    // Get current user to filter by user-specific files
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated to list files');
    }

    // For now, return empty array as S3 list operations need additional implementation
    // In a production setup, you would implement ListObjectsV2 command with user prefix
    
    return [];
  } catch (error) {
    
    throw error;
  }
}

/**
 * Gets the public URL for a file
 * Note: Since we're using private buckets, this returns the original URL
 * For secure access, use getSignedDownloadUrl instead
 */
export function getPublicUrl(
  bucket: BucketType,
  path: string
): string {
  const bucketName = BUCKET_NAMES[bucket];
  return `https://sjyldlubmtfoujonmtru.supabase.co/storage/v1/object/public/${bucketName}/${path}`;
}

/**
 * Get a signed URL for a file path (for private buckets)
 * This function takes a file path and generates a fresh signed URL
 * @param filePath The file path (without bucket name)
 * @param bucket The storage bucket type
 * @param expiresInSeconds How long the signed URL should be valid (default: 24 hours)
 * @returns Signed URL for secure access
 */
export async function getSignedUrlFromPath(
  filePath: string,
  bucket: BucketType,
  expiresInSeconds: number = 3600 * 24
): Promise<string> {
  try {
    
    
    // Validate user authentication
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      throw new Error('User must be authenticated to access files');
    }

    const bucketName = BUCKET_NAMES[bucket];
    
    const { data, error } = await supabase.storage
      .from(bucketName)
      .createSignedUrl(filePath, expiresInSeconds);

    if (error) {
      
      throw error;
    }

    if (!data || !data.signedUrl) {
      throw new Error('Failed to generate signed URL');
    }

    
    return data.signedUrl;
  } catch (error) {
    
    throw error;
  }
}

/**
 * Convert a stored URL (signed or public) back to a fresh signed URL
 * This is useful when displaying images that might have expired signed URLs
 * @param storedUrl The URL stored in the database
 * @param bucket The storage bucket type  
 * @param expiresInSeconds How long the new signed URL should be valid (default: 24 hours)
 * @returns Fresh signed URL for secure access
 */
export async function refreshSignedUrl(
  storedUrl: string,
  bucket: BucketType,
  expiresInSeconds: number = 3600 * 24
): Promise<string> {
  try {
    
    
    // Extract the file path from the stored URL
    let filePath: string;
    
    if (storedUrl.includes('/storage/v1/object/')) {
      // Extract path from both public and signed URLs
      const bucketName = BUCKET_NAMES[bucket];
      const bucketPattern = new RegExp(`/storage/v1/object/(?:public|sign)/(?:[^/]+/)?${bucketName}/(.+?)(?:\\?|$)`);
      const match = storedUrl.match(bucketPattern);
      
      if (match && match[1]) {
        filePath = match[1];
      } else {
        throw new Error(`Could not extract file path from URL: ${storedUrl}`);
      }
    } else {
      throw new Error(`Invalid storage URL format: ${storedUrl}`);
    }
    
    
    
    // Generate fresh signed URL
    return await getSignedUrlFromPath(filePath, bucket, expiresInSeconds);
  } catch (error) {
    
    // Return original URL as fallback
    return storedUrl;
  }
}

// Export bucket names for external use
export { BUCKET_NAMES }; 